import vue from 'views/components/mm_pro/empresa/spa_mm_pro.js';
import { PushNotifications } from '@capacitor/push-notifications';
import { FCM as FCMPlugin } from '@capacitor-community/fcm';
import moment from 'moment';

import {
  init_local_notification,
  show_local_notificacion,
  remover_local_notifications,
} from 'views/components/mm_pro/components/notificaciones/local_notification.js';
import {
  guardar_sesion_local_storage,
  get_sesion_local_storage,
} from 'views/components/mm_pro/components/sesion.js';

const REFRESH_INTERVAL_MS = 24 * 60 * 60 * 1000; // 24h

const state = {
  listenersRegistrados: false,
  resumeRegistrado: false,
  refrescoTokenPromesa: null,
};

async function verificarPermisoNotificaciones() {
  let permiso = await PushNotifications.checkPermissions();

  if (permiso.receive === 'prompt') {
    permiso = await PushNotifications.requestPermissions();
  }

  return permiso.receive === 'granted';
}

function guardar_tiempo_ultima_actualizacion() {
  const timestamp = new Date().getTime();
  localStorage.setItem('ultima_actualizacion_token_fcm', timestamp);
}

function obtener_tiempo_ultima_actualizacion() {
  const timestamp = localStorage.getItem('ultima_actualizacion_token_fcm');
  return timestamp ? parseInt(timestamp, 10) : null;
}

function es_necesario_actualizar_token(intervalo_ms) {
  const ultimaActualizacion = obtener_tiempo_ultima_actualizacion();
  const ahora = new Date().getTime();

  if (!ultimaActualizacion) {
    return true;
  }

  const tiempo_transcurrido = moment(ahora).diff(moment(ultimaActualizacion), 'minutes');
  console.log(
    'Minutos transcurridos desde la última actualización del token FCM:',
    tiempo_transcurrido,
  );

  return ahora - ultimaActualizacion > intervalo_ms;
}

async function ensure_permiso_notificaciones({ toastOnDeny = false } = {}) {
  const permiso = await verificarPermisoNotificaciones();

  if (!permiso && toastOnDeny) {
    vue.$bvToast.toast(
      'Para recibir notificaciones activa los permisos desde la configuración de tu dispositivo, dirígete a Configuración > Aplicaciones > Mejores Mudanzas Pro > Permisos',
      {
        title: 'Activar Notificaciones',
        variant: 'info',
        solid: true,
        toaster: 'b-toaster-top-center',
      },
    );
  }

  return permiso;
}

async function guardar_token_fcm(sesion, token_fcm) {
  if (!sesion?.token || !sesion?.id) {
    console.error('Sesión inválida al guardar token FCM');
    return false;
  }

  try {
    await vue.$http.post(
      `${api_url}graphql?token=${sesion.token}&id=${sesion.id}`,
      {
        query: /* GraphQL */ `
          mutation ($input: token_fcm_input_upsert!) {
            unique_insert_token_fcm(input: $input) {
              id
            }
          }
        `,
        variables: {
          input: {
            token: token_fcm,
            id_empresa: sesion.id,
          },
        },
      },
      { timeout: 30000 },
    );
    return true;
  } catch (error) {
    console.error('Error al guardar token FCM:', JSON.stringify(error));
    return false;
  }
}

async function persistir_token_local_y_backend(sesion, token) {
  sesion.token_fcm = token;
  guardar_sesion_local_storage(sesion);

  const guardado = await guardar_token_fcm(sesion, token);
  if (!guardado) {
    return false;
  }

  guardar_tiempo_ultima_actualizacion();
  return true;
}

async function obtener_token_inicial(sesion) {
  const permiso = await ensure_permiso_notificaciones({ toastOnDeny: true });
  if (!permiso) {
    return null;
  }

  try {
    const token = await FCMPlugin.getToken();

    if (!token?.token) {
      console.error('No se obtuvo un token de FCM válido');
      return null;
    }

    const guardado = await persistir_token_local_y_backend(sesion, token.token);
    if (!guardado) {
      console.error('No se pudo guardar el token FCM al obtenerlo');
      return null;
    }

    return token.token;
  } catch (error) {
    console.error('Error al obtener el token FCM:', JSON.stringify(error));
    vue.$bvToast.toast(
      'Hubo un error al activar las notificaciones. Por favor, intenta nuevamente.',
      {
        title: 'Error de Notificaciones',
        variant: 'danger',
        solid: true,
        toaster: 'b-toaster-top-center',
      },
    );
    return null;
  }
}

async function refrescar_token_si_necesario(sesion) {
  if (state.refrescoTokenPromesa) {
    return state.refrescoTokenPromesa;
  }

  if (!sesion?.token_fcm) {
    return null;
  }

  if (!es_necesario_actualizar_token(REFRESH_INTERVAL_MS)) {
    return null;
  }

  state.refrescoTokenPromesa = (async () => {
    if (!FCMPlugin?.refreshToken) {
      console.warn('Refresh FCM no disponible en esta plataforma');
      return null;
    }

    try {
      const token = await FCMPlugin.refreshToken();

      if (!token?.token) {
        console.error('Error al actualizar el token FCM: no se obtuvo un nuevo token.');
        return null;
      }

      const guardado = await persistir_token_local_y_backend(sesion, token.token);
      if (!guardado) {
        console.error('No se pudo guardar el token FCM en backend tras refresh');
        return null;
      }

      console.log('Token FCM actualizado exitosamente');
      return token.token;
    } catch (error) {
      console.error('Error al refrescar el token FCM:', error?.message || error);

      // Fallback para emuladores sin Play Services o fallos de deleteToken
      try {
        const token = await FCMPlugin.getToken();

        if (!token?.token) {
          console.error('No se obtuvo token FCM al usar getToken como respaldo');
          return null;
        }

        const guardado = await persistir_token_local_y_backend(sesion, token.token);
        if (!guardado) {
          console.error(
            'No se pudo guardar el token FCM en backend tras fallback getToken',
          );
          return null;
        }

        console.log('Token FCM obtenido con getToken tras fallo de refresh');
        return token.token;
      } catch (fallbackError) {
        console.error(
          'Falló getToken tras intentar refrescar FCM:',
          fallbackError?.message || fallbackError,
        );
        return null;
      }
    }
  })();

  try {
    return await state.refrescoTokenPromesa;
  } finally {
    state.refrescoTokenPromesa = null;
  }
}

async function bootstrap_token_flow() {
  const sesion = get_sesion_local_storage();
  if (!sesion) {
    return;
  }

  if (!sesion.token_fcm) {
    await obtener_token_inicial(sesion);
    return;
  }

  await refrescar_token_si_necesario(sesion);
}

function registrar_listeners_si_faltan() {
  if (state.listenersRegistrados) {
    return;
  }

  PushNotifications.addListener('pushNotificationReceived', async (notification) => {
    show_local_notificacion(notification);
  });

  PushNotifications.addListener(
    'pushNotificationActionPerformed',
    async (notification) => {
      if (notification.notification.data.ruta) {
        vue.$router.push({ path: notification.notification.data.ruta });
      }
    },
  );

  state.listenersRegistrados = true;
}

async function iniciar_notificaciones_push() {
  const permiso = await ensure_permiso_notificaciones();
  if (!permiso) {
    return;
  }

  await init_local_notification(vue);
  registrar_listeners_si_faltan();
  registrar_resume_si_falta();
  await bootstrap_token_flow();
}

async function remover_fcm() {
  await PushNotifications.removeAllDeliveredNotifications();
  await PushNotifications.removeAllListeners();
  await remover_local_notifications();

  state.listenersRegistrados = false;

  try {
    await FCMPlugin.deleteInstance();
    console.log('Instancia de FCM eliminada correctamente');
  } catch (error) {
    console.error('Error al eliminar la instancia de FCM:', JSON.stringify(error));
  }
}

async function remover_token_fcm() {
  const sesion = get_sesion_local_storage();

  if (!sesion?.token_fcm || !sesion?.token || !sesion?.id) {
    console.warn('No hay sesión/token FCM para remover');
    return;
  }

  try {
    await vue.$http.post(
      `${api_url}graphql?token=${sesion.token}&id=${sesion.id}`,
      {
        query: /* GraphQL */ `
          mutation ($input: token_fcm_input_delete!) {
            delete_token_fcm(input: $input) {
              id
            }
          }
        `,
        variables: {
          input: {
            token: sesion.token_fcm,
            id_empresa: sesion.id,
          },
        },
      },
      { timeout: 30000 },
    );
  } catch (error) {
    console.error('Error al eliminar token FCM en backend:', JSON.stringify(error));
  }
}

// Actualiza el token cuando la aplicación se reanuda
const on_resume_actualizar_token = async () => {
  const sesion = get_sesion_local_storage();
  if (!sesion) {
    return;
  }

  await bootstrap_token_flow();
};

function registrar_resume_si_falta() {
  if (state.resumeRegistrado) {
    return;
  }

  document.addEventListener('resume', on_resume_actualizar_token);
  state.resumeRegistrado = true;
}

async function obtener_y_guardar_token_fcm(sesion) {
  return obtener_token_inicial(sesion);
}

export {
  iniciar_notificaciones_push,
  obtener_y_guardar_token_fcm,
  remover_fcm,
  remover_token_fcm,
};
